import React, { useState, useEffect } from "react";
import { Star, X } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES } from "../mock";
import { api } from "../lib/api";

const Gallery = () => {
  const { t, lang } = useLanguage();
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.listGallery().then(setGallery).catch(() => {});
    api.listTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  const categories = ["All", ...Array.from(new Set(gallery.map((g) => g.category)))];
  const filtered = active === "All" ? gallery : gallery.filter((g) => g.category === active);
  const gTitle = (g) => (lang === "en" && g.title_en ? g.title_en : g.title_pt);
  const catLabel = (cat) => (t(`cat_${cat}`) !== `cat_${cat}` ? t(`cat_${cat}`) : cat);

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-40 pb-24 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.galleryHero} alt="Gallery hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070b18]/85 via-[#070b18]/85 to-[#070b18]" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mx-auto bf-section-fade">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4">{t("gp_title")}</h1>
          <p className="text-lg md:text-xl text-gray-300">{t("gp_subtitle")}</p>
        </div>
      </section>

      {/* Filters + grid */}
      <section className="py-16 px-6 bg-[#0a1024]">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  active === cat
                    ? "bg-gradient-to-r from-[#67e8f9] to-[#22d3ee] text-[#0a1024]"
                    : "bg-[#131b32] text-gray-300 hover:bg-[#1a2340] border border-white/5"
                }`}
              >
                {catLabel(cat)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filtered.map((g) => (
              <button
                key={g.id}
                onClick={() => setLightbox(g)}
                className="relative rounded-xl overflow-hidden group border border-white/10 aspect-square"
              >
                <img src={g.image} alt={gTitle(g)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070b18] via-transparent to-transparent opacity-0 group-hover:opacity-90 transition-opacity duration-300 flex items-end p-4">
                  <div>
                    <span className="text-[#67e8f9] text-xs uppercase tracking-wide">{catLabel(g.category)}</span>
                    <p className="text-white font-semibold">{gTitle(g)}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold bf-cyan-text mb-3">{t("testimonials_title")}</h2>
            <p className="text-gray-400">{t("testimonials_subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((tm) => (
              <div key={tm.id} className="bf-glass bf-card-hover rounded-2xl p-8 text-center">
                <div className="flex justify-center gap-1 mb-5">
                  {[...Array(tm.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" />
                  ))}
                </div>
                <p className="text-gray-300 italic leading-relaxed mb-6">"{tm.text}"</p>
                <h4 className="text-white font-bold">{tm.name}</h4>
                <p className="text-[#67e8f9] text-sm">{tm.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
        >
          <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setLightbox(null)}>
            <X className="w-8 h-8" />
          </button>
          <img src={lightbox.image} alt={gTitle(lightbox)} className="max-w-full max-h-[85vh] rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default Gallery;
